import { useState, useRef, useCallback, useEffect } from 'react';
import { format, isToday, isSameMonth } from 'date-fns';
import { Copy, Trash2, Plus, GripVertical } from 'lucide-react';

// Helper function to get job type color
const getJobTypeColor = (projectType) => {
  if (projectType === 'CON') {
    return { text: 'text-blue-400', border: '#3B82F6' }; // Blue for Construction
  }
  return { text: 'text-[#F5A623]', border: '#F5A623' }; // Orange for Developments (default)
};

// Helper function to format the project display
const formatProjectDisplay = (schedule) => {
  const ref = schedule.project_ref || '';
  const parts = ref.split('-');
  
  if (parts.length >= 3) {
    const jobRef = parts[0];
    const jobType = parts[1];
    const jobName = parts.slice(2).join('-');
    return {
      line1: `${jobRef} – ${jobType}`,
      line2: jobName
    };
  }
  
  return {
    line1: ref.split(' - ')[0] || ref,
    line2: ref.split(' - ')[1] || ''
  };
};

export const ScheduleMonthView = ({
  days,
  currentDate,
  canManageProjects,
  projectColorMap,
  employeeColorMap,
  PROJECT_COLORS,
  EMPLOYEE_COLORS,
  draggedSchedule,
  draggedProject,
  getSchedulesForDay,
  getInitials,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDrop,
  onDuplicate,
  onDelete,
  onAddClick,
  onEdit,
}) => {
  // Track expanded day info
  const [expandedDay, setExpandedDay] = useState(null);
  const [expandedDayData, setExpandedDayData] = useState(null);
  const [panelPosition, setPanelPosition] = useState({ top: 0, left: 0 });
  const [isOverPanel, setIsOverPanel] = useState(false);
  const closeTimeoutRef = useRef(null);
  const containerRef = useRef(null);

  // Clear any pending timeout
  const clearCloseTimeout = useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  }, []);

  // Schedule close with delay
  const scheduleClose = useCallback(() => {
    clearCloseTimeout();
    closeTimeoutRef.current = setTimeout(() => {
      setExpandedDay(null);
      setExpandedDayData(null);
    }, 150);
  }, [clearCloseTimeout]);

  // Handle mouse enter on day cell
  const handleDayCellEnter = useCallback((e, day, daySchedules, idx) => {
    clearCloseTimeout();
    
    if (daySchedules.length === 0) {
      return;
    }
    
    const dayKey = format(day, 'yyyy-MM-dd');
    const rect = e.currentTarget.getBoundingClientRect();
    const containerRect = containerRef.current?.getBoundingClientRect() || { left: 0, top: 0 };
    
    // Calculate panel position relative to container
    let left = rect.left - containerRect.left;
    // Adjust for right-side columns
    if (idx % 7 >= 5) {
      left = left - 140; // Shift left for right columns
    }
    
    setPanelPosition({
      top: rect.top - containerRect.top,
      left: left
    });
    
    setExpandedDay(dayKey);
    setExpandedDayData({
      day,
      daySchedules,
      isCurrentDay: isToday(day),
      isCurrentMonth: isSameMonth(day, currentDate),
      idx
    });
  }, [clearCloseTimeout, currentDate]);

  // Handle mouse leave on day cell
  const handleDayCellLeave = useCallback(() => {
    scheduleClose();
  }, [scheduleClose]);

  // Handle mouse enter on expanded panel
  const handlePanelEnter = useCallback(() => {
    clearCloseTimeout();
    setIsOverPanel(true);
  }, [clearCloseTimeout]);

  // Handle mouse leave on expanded panel  
  const handlePanelLeave = useCallback(() => {
    setIsOverPanel(false);
    scheduleClose();
  }, [scheduleClose]);

  // Cleanup on unmount
  useEffect(() => {
    return () => clearCloseTimeout();
  }, [clearCloseTimeout]);

  return (
    <div ref={containerRef} className="bg-[#2A3240] rounded-lg border border-white/10 overflow-visible relative">
      <div className="grid grid-cols-7 border-b border-white/10">
        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
          <div key={day} className="p-2 text-center text-xs font-semibold text-gray-500 uppercase">{day}</div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {days.map((day, idx) => {
          const daySchedules = getSchedulesForDay(day);
          const isCurrentDay = isToday(day);
          const isCurrentMonth = isSameMonth(day, currentDate);
          const dayKey = format(day, 'yyyy-MM-dd');
          const hasMoreShifts = daySchedules.length > 3;

          return (
            <div 
              key={dayKey}
              data-testid={`month-cell-${dayKey}`}
              className={`min-h-[100px] md:min-h-[120px] p-1 border-b border-r border-white/5 flex flex-col
                ${!isCurrentMonth ? 'bg-[#1E2530]/50' : ''} 
                ${isCurrentDay ? 'bg-[#F5A623]/5' : ''} 
                ${(draggedSchedule || draggedProject) ? 'border-dashed border-[#F5A623]/50 bg-[#F5A623]/5' : ''}`}
              onDragOver={onDragOver} 
              onDrop={(e) => onDrop(e, day)}
              onMouseEnter={(e) => handleDayCellEnter(e, day, daySchedules, idx)}
              onMouseLeave={handleDayCellLeave}
            >
              <div className={`text-sm p-1 ${isCurrentDay ? 'text-[#F5A623] font-bold' : isCurrentMonth ? 'text-white' : 'text-gray-600'}`}>
                {format(day, 'd')}
              </div>
              
              {/* Normal collapsed view */}
              <div className="space-y-1 flex-1">
                {daySchedules.slice(0, 3).map((schedule) => {
                  const jobTypeColor = getJobTypeColor(schedule.project_type);
                  const projectDisplay = formatProjectDisplay(schedule);
                  const employeeNames = schedule.employees.map(e => e.name).join(', ');
                  const tooltipText = `${schedule.project_ref}\n\nAssigned: ${employeeNames || 'No employees assigned'}`;
                  return (
                    <div key={schedule.id} draggable={canManageProjects} onDragStart={(e) => onDragStart(e, schedule)}
                      onDragEnd={onDragEnd}
                      onDoubleClick={(e) => { e.stopPropagation(); onEdit && onEdit(schedule); }}
                      className={`px-1.5 py-0.5 rounded text-xs truncate cursor-move border-l-4 group relative bg-[#1E2530] border-r border-t border-b border-white/10 ${draggedSchedule?.id === schedule.id ? 'opacity-50' : ''}`}
                      style={{ borderLeftColor: jobTypeColor.border }}
                      title={`${tooltipText}\n\nDouble-click to edit`}>
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0 truncate">
                          <span className={`font-medium ${jobTypeColor.text}`}>{projectDisplay.line1}</span>
                          <span className="text-gray-400 ml-1">{employeeNames || '-'}</span>
                        </div>
                        {canManageProjects && (
                          <div className="hidden group-hover:flex items-center gap-0.5 ml-1">
                            <button onClick={(e) => { e.stopPropagation(); onDuplicate(schedule, day); }}
                              className="p-0.5 text-gray-400 hover:text-[#F5A623]"><Copy className="w-2.5 h-2.5" /></button>
                            <button onClick={(e) => { e.stopPropagation(); onDelete(schedule.id, e); }}
                              className="p-0.5 text-gray-400 hover:text-red-400"><Trash2 className="w-2.5 h-2.5" /></button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
                {hasMoreShifts && (
                  <div className="px-1.5 text-xs text-[#F5A623] font-medium">
                    +{daySchedules.length - 3} more
                  </div>
                )}
              </div>
              
              {/* Add button */}
              {canManageProjects && isCurrentMonth && (
                <button 
                  onClick={(e) => { e.stopPropagation(); onAddClick(day); }}
                  data-testid={`month-add-btn-${format(day, 'yyyy-MM-dd')}`}
                  className="mt-1 w-full py-1 border border-dashed border-white/20 rounded text-gray-500 hover:border-[#F5A623] hover:text-[#F5A623] flex items-center justify-center gap-0.5 transition-colors"
                >
                  <Plus className="w-2.5 h-2.5" />
                  <span className="text-[10px]">Add</span>
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* Expanded panel - rendered OUTSIDE the grid to avoid hover conflicts */}
      {expandedDay && expandedDayData && (
        <div 
          className="absolute bg-[#2A3240] border-2 border-[#F5A623] rounded-lg shadow-2xl z-[100]"
          style={{ 
            top: panelPosition.top,
            left: panelPosition.left,
            minHeight: '180px',
            width: '280px',
          }}
          onMouseEnter={handlePanelEnter}
          onMouseLeave={handlePanelLeave}
        >
          {/* Expanded header */}
          <div className={`p-3 border-b border-[#F5A623]/30 ${expandedDayData.isCurrentDay ? 'bg-[#F5A623]/10' : ''}`}>
            <div className="text-xs text-gray-500 uppercase">{format(expandedDayData.day, 'EEEE')}</div>
            <div className={`text-lg font-bold ${expandedDayData.isCurrentDay ? 'text-[#F5A623]' : 'text-white'}`}>
              {format(expandedDayData.day, 'd MMMM')}
            </div>
            <div className="text-xs text-gray-400 mt-0.5">{expandedDayData.daySchedules.length} shift{expandedDayData.daySchedules.length !== 1 ? 's' : ''}</div>
          </div>

          {/* All shifts in Week View style */}
          <div className="p-2 space-y-2 max-h-[320px] overflow-y-auto">
            {expandedDayData.daySchedules.map((schedule) => {
              const jobTypeColor = getJobTypeColor(schedule.project_type);
              const projectDisplay = formatProjectDisplay(schedule);
              return (
                <div 
                  key={schedule.id} 
                  draggable={canManageProjects} 
                  onDragStart={(e) => onDragStart(e, schedule)}
                  onDragEnd={onDragEnd}
                  onDoubleClick={(e) => { e.stopPropagation(); onEdit && onEdit(schedule); }}
                  title="Double-click to edit"
                  className={`p-2 rounded border-l-4 transition-all group cursor-move bg-[#1E2530] border border-white/10 ${draggedSchedule?.id === schedule.id ? 'opacity-50' : ''}`}
                  style={{ borderLeftColor: jobTypeColor.border }}
                >
                  <div className="flex items-start justify-between gap-1">
                    <div className="flex-1 min-w-0">
                      {canManageProjects && <GripVertical className="w-3 h-3 text-gray-500 mb-1 cursor-grab" />}
                      <p className={`text-xs font-semibold truncate ${jobTypeColor.text}`}>{projectDisplay.line1}</p>
                      <p className="text-xs text-gray-400 truncate mt-0.5">{projectDisplay.line2}</p>
                    </div>
                    {canManageProjects && (
                      <div className="flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button onClick={(e) => { e.stopPropagation(); onDuplicate(schedule, expandedDayData.day); }}
                          className="p-1 text-gray-400 hover:text-[#F5A623]"><Copy className="w-3 h-3" /></button>
                        <button onClick={(e) => { e.stopPropagation(); onDelete(schedule.id, e); }}
                          className="p-1 text-gray-400 hover:text-red-400"><Trash2 className="w-3 h-3" /></button>
                      </div>
                    )}
                  </div>
                  <div className="mt-1.5 space-y-0.5">
                    {schedule.employees.map(emp => {
                      const empColor = employeeColorMap?.[emp.id] || EMPLOYEE_COLORS?.[0] || { bg: 'bg-blue-500/20', text: 'text-blue-400', border: 'border-blue-500/30' };
                      return (
                        <div key={emp.id} className={`px-1.5 py-0.5 rounded text-[10px] font-medium truncate ${empColor.bg} ${empColor.text} border ${empColor.border}`}>
                          {emp.name}
                        </div>
                      );
                    })}
                    {schedule.employees.length === 0 && (
                      <div className="text-[10px] text-gray-500 italic">No employees assigned</div>
                    )}
                  </div>
                </div>
              );
            })}

            {canManageProjects && expandedDayData.isCurrentMonth && (
              <button 
                onClick={(e) => { e.stopPropagation(); onAddClick(expandedDayData.day); }}
                className="w-full p-2 border border-dashed border-white/20 rounded text-gray-500 hover:border-[#F5A623] hover:text-[#F5A623] flex items-center justify-center gap-1 transition-colors"
              >
                <Plus className="w-3 h-3" />
                <span className="text-xs">Add Shift</span>
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
